import { useState, useRef, useEffect, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { LuFilter } from "react-icons/lu";
import { IoMdClose } from "react-icons/io";
import { TbSend, TbSendOff, TbMail, TbClipboardCheck, TbClipboardX, TbReceiptTax, TbAlertTriangle, TbUpload, TbPencil } from "react-icons/tb";
import "./invoiceFilterBar.scss";
import { useFitToViewport } from "src/hooks/useFitToViewport";
import { sanitizeInput } from "src/helpers/sanitizeInput";
import { getTagStyle } from "src/helpers/randomColor";
import { invoiceOwnerLabel } from "src/helpers/invoiceOwner";
import type { Tag } from "src/types";

export type InvoiceFilters = {
    sent: "all" | "sent" | "unsent";
    amountMin: string;
    dunning: string;
    status: string;
    /** Matches account holder or billed patient name. */
    patients: string;
    createdFrom: string;
    createdTo: string;
    questionnaireSubmitted: boolean;
    questionnaireNotSubmitted: boolean;
    questionnaireNotSent: boolean;
    vatOnly: boolean;
    goaConflict: boolean;
    amountMismatch: boolean;
    /** Unsent invoices that can only go out by post and are ready to — see isReadyForPost (useInvoiceList.tsx). */
    readyForPost: boolean;
    /** tag id — admin-only filter (matches invoices whose tagIds includes this id), only ever set when tags are available. */
    tag: string;
    /** Owner doctor id ("system" = desktop-app imports) — only offered when the viewer sees several doctors' invoices. */
    doctor: string;
    /** "manual" = created by hand (chapter 18), "upload" = imported (PAD upload / desktop app). Uses the
     *  backend's `manualInvoice` flag, never `source` (old imports read as "manual" there). */
    origin: "" | "upload" | "manual";
};

export const DEFAULT_FILTERS: InvoiceFilters = {
    sent: "all",
    amountMin: "",
    dunning: "",
    status: "",
    patients: "",
    createdFrom: "",
    createdTo: "",
    questionnaireSubmitted: false,
    questionnaireNotSubmitted: false,
    questionnaireNotSent: false,
    vatOnly: false,
    goaConflict: false,
    amountMismatch: false,
    readyForPost: false,
    tag: "",
    doctor: "",
    origin: "",
};

/** Origin filter — shared by both invoice list filters (Invoices.tsx, useInvoiceList.tsx). */
export const matchesOrigin = (origin: InvoiceFilters["origin"], manualInvoice: boolean | undefined) =>
    origin === "" || (origin === "manual") === !!manualInvoice;

interface Props {
    filters: InvoiceFilters;
    onChange: (f: InvoiceFilters) => void;
    onClearSearch?: () => void;
    isPro?: boolean;
    tags?: Tag[];
    /** Praxis has post sending enabled — the "ready for post" chip is meaningless otherwise. */
    postEnabled?: boolean;
    /** Owners of the visible invoices — passed only when the viewer sees several doctors' invoices. */
    doctors?: InvoiceOwnerOption[];
}

export interface InvoiceOwnerOption {
    id: string;
    name: string | null;
}

export default function InvoiceFilterBar({ filters, onChange, onClearSearch, isPro, tags, postEnabled, doctors }: Props) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    useFitToViewport(panelRef, open);
    const { t } = useTranslation();

    const DUNNING_OPTIONS = [
        { value: 0, label: t("filters.dunningLevels.none"),      modifier: "grey",              },
        { value: 1, label: t("filters.dunningLevels.friendly"),  modifier: "green",  badge: "M1" },
        { value: 2, label: t("filters.dunningLevels.demanding"), modifier: "orange", badge: "M2" },
        { value: 3, label: t("filters.dunningLevels.final"),     modifier: "red",    badge: "M3" },
        { value: 4, label: t("filters.dunningLevels.legal"),     modifier: "blue",              },
    ];

    const STATUS_OPTIONS = [
        { value: 0, label: t("filters.status.open"),       modifier: "grey"   },
        { value: 2, label: t("filters.status.paid"),       modifier: "green"  },
        { value: 4, label: t("filters.status.overdue"),    modifier: "red"    },
        { value: 5, label: t("filters.status.writtenOff"), modifier: "purple" },
    ];

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const activeCount = [
        filters.sent !== "all",
        filters.amountMin !== "",
        filters.dunning !== "",
        filters.status !== "",
        filters.patients !== "",
        filters.createdFrom !== "",
        filters.createdTo !== "",
        filters.questionnaireSubmitted,
        filters.questionnaireNotSubmitted,
        filters.questionnaireNotSent,
        filters.vatOnly,
        filters.goaConflict,
        filters.amountMismatch,
        filters.readyForPost,
        filters.tag !== "",
        filters.doctor !== "",
        filters.origin !== "",
    ].filter(Boolean).length;

    const set = <K extends keyof InvoiceFilters>(key: K, val: InvoiceFilters[K]) =>
        onChange({ ...filters, [key]: val });

    return (
        <div className="inv-filter" ref={ref}>
            <button
                className={`inv-filter__btn${activeCount > 0 ? " inv-filter__btn--active" : ""}${open ? " inv-filter__btn--open" : ""}`}
                onClick={() => setOpen((o) => !o)}
            >
                <LuFilter />
                {t("filters.filters")}
                {activeCount > 0 && (
                    <span className="inv-filter__badge">{activeCount}</span>
                )}
            </button>

            {open && (
                <div className="inv-filter__panel" ref={panelRef}>
                    <div className="inv-filter__panel-header">
                        <span>{t("filters.filters")}</span>
                        {activeCount > 0 && (
                            <button
                                className="inv-filter__clear"
                                onClick={() => { onChange(DEFAULT_FILTERS); onClearSearch?.(); }}
                            >
                                <IoMdClose /> {t("filters.clearAll")}
                            </button>
                        )}
                    </div>

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("filters.sendingStatus")}</span>
                        <div className="inv-filter__chips">
                            <button
                                className={`inv-filter__chip${filters.sent === "all" ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("sent", "all")}
                            >
                                {t("filters.all")}
                            </button>
                            <button
                                className={`inv-filter__chip inv-filter__chip--green${filters.sent === "sent" ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("sent", "sent")}
                            >
                                <TbSend />{t("filters.sent")}
                            </button>
                            <button
                                className={`inv-filter__chip inv-filter__chip--neutral${filters.sent === "unsent" ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("sent", "unsent")}
                            >
                                <TbSendOff />{t("filters.notSent")}
                            </button>
                            {postEnabled && (
                                <button
                                    className={`inv-filter__chip inv-filter__chip--blue${filters.readyForPost ? " inv-filter__chip--active" : ""}`}
                                    title={t("filters.readyForPostHint")}
                                    onClick={() => set("readyForPost", !filters.readyForPost)}
                                >
                                    <TbMail />{t("filters.readyForPost")}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("filters.amountGte")}</span>
                        <input
                            type="number"
                            className="inv-filter__input"
                            aria-label="Minimum amount"
                            placeholder={t("filters.amountPlaceholder")}
                            value={filters.amountMin}
                            onChange={(e) => set("amountMin", e.target.value)}
                        />
                    </div>

                    <div className="inv-filter__row inv-filter__row--wrap">
                        <span className="inv-filter__label">{t("filters.questionnaire")}</span>
                        <div className="inv-filter__chips">
                            <button
                                className={`inv-filter__chip inv-filter__chip--green${filters.questionnaireSubmitted ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("questionnaireSubmitted", !filters.questionnaireSubmitted)}
                            >
                                <TbClipboardCheck />{t("filters.questionnaireSubmitted")}
                            </button>
                            <button
                                className={`inv-filter__chip inv-filter__chip--neutral${filters.questionnaireNotSubmitted ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("questionnaireNotSubmitted", !filters.questionnaireNotSubmitted)}
                            >
                                <TbClipboardX />{t("filters.questionnaireNotSubmitted")}
                            </button>
                            <button
                                className={`inv-filter__chip inv-filter__chip--neutral${filters.questionnaireNotSent ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("questionnaireNotSent", !filters.questionnaireNotSent)}
                            >
                                <TbClipboardX />{t("filters.questionnaireNotSent")}
                            </button>
                        </div>
                    </div>

                    <div className="inv-filter__row inv-filter__row--wrap">
                        <span className="inv-filter__label">{t("filters.vat")}</span>
                        <div className="inv-filter__chips">
                            <button
                                className={`inv-filter__chip inv-filter__chip--blue${filters.vatOnly ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("vatOnly", !filters.vatOnly)}
                            >
                                <TbReceiptTax />{t("filters.vatIncluded")}
                            </button>
                        </div>
                    </div>

                    <div className="inv-filter__row inv-filter__row--wrap">
                        <span className="inv-filter__label">{t("filters.origin")}</span>
                        <div className="inv-filter__chips">
                            <button
                                className={`inv-filter__chip${filters.origin === "" ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("origin", "")}
                            >
                                {t("filters.all")}
                            </button>
                            <button
                                className={`inv-filter__chip inv-filter__chip--neutral${filters.origin === "upload" ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("origin", filters.origin === "upload" ? "" : "upload")}
                            >
                                <TbUpload />{t("filters.originUpload")}
                            </button>
                            <button
                                className={`inv-filter__chip inv-filter__chip--neutral${filters.origin === "manual" ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("origin", filters.origin === "manual" ? "" : "manual")}
                            >
                                <TbPencil />{t("filters.originManual")}
                            </button>
                        </div>
                    </div>

                    <div className="inv-filter__row inv-filter__row--wrap">
                        <span className="inv-filter__label">{t("filters.reviewFlags")}</span>
                        <div className="inv-filter__chips">
                            {isPro && (
                                <button
                                    className={`inv-filter__chip inv-filter__chip--red${filters.goaConflict ? " inv-filter__chip--active" : ""}`}
                                    onClick={() => set("goaConflict", !filters.goaConflict)}
                                >
                                    <TbAlertTriangle />{t("filters.goaConflict")}
                                </button>
                            )}
                            <button
                                className={`inv-filter__chip inv-filter__chip--orange${filters.amountMismatch ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("amountMismatch", !filters.amountMismatch)}
                            >
                                <TbAlertTriangle />{t("filters.amountMismatch")}
                            </button>
                        </div>
                    </div>

                    <div className="inv-filter__row inv-filter__row--wrap">
                        <span className="inv-filter__label">{t("table.dunning")}</span>
                        <div className="inv-filter__chips">
                            <button
                                className={`inv-filter__chip${filters.dunning === "" ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("dunning", "")}
                            >
                                {t("filters.all")}
                            </button>
                            {DUNNING_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    className={`dunning dunning--${opt.modifier} inv-filter__chip-badge${filters.dunning === String(opt.value) ? " inv-filter__chip-badge--active" : ""}`}
                                    onClick={() => set("dunning", filters.dunning === String(opt.value) ? "" : String(opt.value))}
                                >
                                    {opt.badge && <span className="dunning__badge">{opt.badge}</span>}
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="inv-filter__row inv-filter__row--wrap">
                        <span className="inv-filter__label">{t("table.status")}</span>
                        <div className="inv-filter__chips">
                            <button
                                className={`inv-filter__chip${filters.status === "" ? " inv-filter__chip--active" : ""}`}
                                onClick={() => set("status", "")}
                            >
                                {t("filters.all")}
                            </button>
                            {STATUS_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    className={`invoice-status invoice-status--${opt.modifier} inv-filter__chip-badge${filters.status === String(opt.value) ? " inv-filter__chip-badge--active" : ""}`}
                                    onClick={() => set("status", filters.status === String(opt.value) ? "" : String(opt.value))}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("table.patient")}</span>
                        <input
                            type="text"
                            className="inv-filter__input"
                            aria-label={t("invoices.filterPatients")}
                            placeholder={t("filters.search")}
                            value={filters.patients}
                            onChange={(e) => set("patients", sanitizeInput(e.target.value))}
                        />
                    </div>

                    {!!doctors?.length && (
                        <div className="inv-filter__row inv-filter__row--wrap">
                            <span className="inv-filter__label">{t("filters.doctor")}</span>
                            <div className="inv-filter__chips">
                                <button
                                    className={`inv-filter__chip${filters.doctor === "" ? " inv-filter__chip--active" : ""}`}
                                    onClick={() => set("doctor", "")}
                                >
                                    {t("filters.all")}
                                </button>
                                {doctors.map((d) => (
                                    <button
                                        key={d.id}
                                        className={`inv-filter__chip inv-filter__chip--neutral${filters.doctor === d.id ? " inv-filter__chip--active" : ""}`}
                                        onClick={() => set("doctor", filters.doctor === d.id ? "" : d.id)}
                                    >
                                        {invoiceOwnerLabel({ ownerDoctorId: d.id, ownerDoctorName: d.name }, t)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {!!tags?.length && (
                        <div className="inv-filter__row inv-filter__row--wrap">
                            <span className="inv-filter__label">{t("tags.tag")}</span>
                            <div className="inv-filter__chips">
                                {tags.map((tag) => (
                                    <button
                                        key={tag.id}
                                        className={`inv-filter__chip inv-filter__chip--tag${filters.tag === tag.id ? " inv-filter__chip--active" : ""}`}
                                        style={getTagStyle(tag.name) as CSSProperties}
                                        onClick={() => set("tag", filters.tag === tag.id ? "" : tag.id)}
                                    >
                                        {tag.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="inv-filter__row">
                        <span className="inv-filter__label">{t("table.created")}</span>
                        <div className="inv-filter__date-range">
                            <input
                                type="date"
                                className="inv-filter__input inv-filter__input--date"
                                aria-label={t("filters.createdFrom")}
                                value={filters.createdFrom}
                                onChange={(e) => set("createdFrom", e.target.value)}
                            />
                            <span className="inv-filter__date-sep">{t("filters.createdTo")}</span>
                            <input
                                type="date"
                                className="inv-filter__input inv-filter__input--date"
                                aria-label={t("filters.createdTo")}
                                value={filters.createdTo}
                                onChange={(e) => set("createdTo", e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
